import React, { useRef, useState } from 'react'
import { ErrorDialog, InfoCDRLearnMore, LoadingDialog } from '@verifier-user/verifier-cdr'
import { useApplicationContext } from '../../contexts/appContext'

import { WelcomeB2B } from './WelcomeB2B'
import { WelcomeB2BSolar } from './WelcomeB2BSolar'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useNavigate } from 'react-router-dom'

export const Welcome = ({ vendorSettings }) => {
  const [error, setError] = useState(null)
  const [errorShown, setErrorShown] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [userConsented, setUserConsented] = useState(false)
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState()

  const containerRef = useRef(null)
  const currentErrorRef = useRef(null)
  const emailRegex = /^([\w.%+-]+)@([\w-]+\.)+(\w{2,})$/i
  const navigate = useNavigate()

  const { login, formData, vendorData } = useApplicationContext()

  const components = {
    WelcomeB2B,
    WelcomeB2BSolar
  }

  const isValid = () => {
    let canProceed = true
    let message = []

    if (!email) {
      message.push('Email not specified')
      canProceed = false
    }

    if (email && !emailRegex.test(email)) {
      message.push('Invalid email')
      canProceed = false
    }

    if (!userConsented) {
      message.push('Not Consented')
      canProceed = false
    }

    if (!canProceed) {
      setErrorMessage(message)
    }
    return canProceed
  }

  const sendCode = (event) => {
    event.preventDefault()
    const data = formData.current
    if (userConsented) {
      data.businessPurposeConfirmed = 'true'
    } else {
      data.businessPurposeConfirmed = 'false'
    }

    if (isValid()) {
      setProcessing(true)
      const body = {
        email: email,
        data: data
      }

      login({
        body,
        onSuccess: () => {
          setProcessing(false)
          navigate('/code')
        },
        onError: (error) => {
          console.log(error)
          setProcessing(false)
          setError(error)

          currentErrorRef.current = {
            title: 'Server Unavailable An error has occurred',
            retryFunction: () => sendCode(event),
            cancelFunction: () => setErrorShown(false),
            okTitle: 'Try Again',
            cancelTitle: 'Cancel',
            errorContent: (
              <div>
                <p>
                  We're sorry but there has been a connection issue. Please try again, and if the issue persists, start
                  the process again.
                </p>
              </div>
            )
          }
          setErrorShown(true)
        }
      })
    }
  }

  const handleChangeEmail = (event) => {
    setErrorMessage(undefined)
    setEmail(event.target.value)
  }

  const validateEmail = () => {
    const message = []
    if (!email) {
      message.push('Email not specified')
    }
    if (email && !emailRegex.test(email)) {
      message.push('Invalid email')
    }
    if (message.length) {
      setErrorMessage(message)
    }
  }

  const getWelcomeComponent = () => {
    const WelcomeComponent = components[vendorData.welcomeComponent]
    return (
      <WelcomeComponent
        currentErrorRef={currentErrorRef}
        setProcessing={setProcessing}
        setError={setError}
        setUserConsented={setUserConsented}
        userConsented={userConsented}
        sendCode={sendCode}
        errorMessage={errorMessage}
        email={email}
        handleChangeEmail={handleChangeEmail}
        validateEmail={validateEmail}
      />
    )
  }

  return (
    <>
      <main ref={containerRef}>
        <LoadingDialog id={'fnf-welcome'} isOpen={processing} />
        <ErrorDialog
          id='error'
          error={error}
          title={currentErrorRef.current && currentErrorRef.current.title}
          open={errorShown}
          setOpen={setErrorShown}
          showContent={true}
          cancelHandler={currentErrorRef.current && currentErrorRef.current.cancelFunction}
          okTitle={currentErrorRef.current && currentErrorRef.current.okTitle ? currentErrorRef.current.okTitle : 'Ok'}
          cancelTitle={
            currentErrorRef.current && currentErrorRef.current.cancelTitle
              ? currentErrorRef.current.cancelTitle
              : 'Cancel'
          }
          retryHandler={currentErrorRef.current ? currentErrorRef.current.retryFunction : undefined}
        >
          {currentErrorRef.current && currentErrorRef.current.errorContent}
        </ErrorDialog>

        <div className='border-bdrColor bg-bgColor font-body relative'>
          <div className='cq-w-sm:max-w-screen-sm cq-w-md:max-w-screen-md cq-w-lg:max-w-screen-lg cq-w-xl:max-w-screen-xl cq-w-2xl:max-w-screen-2xl mx-auto px-4 cq-w-sm:flex justify-start cq-w-sm:h-[327px]'>
            <div className='cq-w-sm:flex-none cq-w-sm:w-[60%] py-6 cq-w-sm:py-5 cq-w-md:py-8'>
              <h1 data-testid='welcome-heading' className='text-4xl sm:text-5xl my-0 leading-tight'>
                Get Energy View
              </h1>
              <p className='mt-4 mb-8' data-testid='welcome-sub-heading'>
                <span className='text-md sm:text-lg'>
                  Welcome to Verifier — the privacy-friendly way to view your Energy data.
                </span>
              </p>
            </div>
          </div>
          <div className="cq-w-sm:absolute right-0 top-0 cq-w-sm:min-w-[40%] h-48 cq-w-sm:min-h-[327px] bg-[url('assets/img/banner@2x-80.jpg')] bg-no-repeat bg-cover bg-center" />
          <div className="hidden cq-w-sm:block absolute right-0 top-0 min-w-[40%] min-h-[327px] bg-[url('assets/img/banner-mask.png')] bg-[length:23px] bg-no-repeat bg-left" />
        </div>
        <div className='cq-w-sm:max-w-screen-sm cq-w-md:max-w-screen-md cq-w-lg:max-w-screen-lg cq-w-xl:max-w-screen-xl cq-w-2xl:max-w-screen-2xl cq-w-sm:flex mx-auto py-4 cq-w-sm:py-8 px-4 font-body'>
          {getWelcomeComponent()}

          <section className='cq-w-sm:w-2/5 flex-auto mt-8 cq-w-sm:mt-0'>
            <div className='bg-bgColor rounded-lg px-8 pt-4 pb-6 mb-4'>
              <h1 className='font-fancy text-[23px] cq-w-lg:text-[34px] italic text-bdrColor leading-tight my-0'>
                The Verifier promise
              </h1>
              <ul className='text-base mt-4' data-testid='verifier-promise-items'>
                <li className='flex mb-2'>
                  <CheckCircleIcon className='w-7 h-7 text-green-600 inline flex-none mr-1' />
                  <span className='flex-auto'>This is a fast, free and secure service</span>
                </li>
                <li className='flex mb-2'>
                  <CheckCircleIcon className='w-7 h-7 text-green-600 inline flex-none mr-1' />
                  <span className='flex-auto'>
                    {vendorSettings.viewSettings.whiteLabel ? <span>Verifier</span> : <span>We</span>} won’t access your
                    data without your consent
                  </span>
                </li>
              </ul>
            </div>
            <div className='px-8 py-4'>
              <InfoCDRLearnMore />
            </div>
          </section>
        </div>
      </main>
    </>
  )
}
